/**
 * Execution Monitoring API Queries
 * All monitoring calls now go through Admin Portal API (8095)
 * SSE streaming still requires direct connection to Execution Engine
 */

import { fetchApi, MONITORING_API, EXECUTION_API_BASE } from '@/api/client'
import type {
  ExecutionProgress,
  ExecutionSummary,
  ActiveExecutionCount,
  CampaignMetrics,
  DashboardHealth,
  DashboardCatchupResponse,
  DashboardEvent,
  TriggerExecutionResponse,
  ExecutionMonitoringResponse,
  ExecutionDetailResponse,
  CampaignExecutionHistoryResponse,
  MonitoringStatsResponse,
  CampaignType,
  AccountSummary,
} from '../types'

// ============================================================================
// Execution Progress Endpoints - via Admin Portal Monitoring API
// ============================================================================

/**
 * Get full execution progress details
 * Uses the new progress endpoint with comprehensive metrics for execution details page.
 */
export async function getExecutionProgress(executionId: string): Promise<ExecutionProgress> {
  const encodedId = encodeURIComponent(executionId)
  return fetchApi<ExecutionProgress>(`${MONITORING_API}/executions/progress?executionId=${encodedId}`)
}

/**
 * Get execution progress summary
 */
export async function getExecutionSummary(executionId: string): Promise<ExecutionSummary> {
  const encodedId = encodeURIComponent(executionId)
  return fetchApi<ExecutionSummary>(`${MONITORING_API}/executions/${encodedId}`)
}

/**
 * Get all active executions
 */
export async function getActiveExecutions(): Promise<ExecutionSummary[]> {
  return fetchApi<ExecutionSummary[]>(`${MONITORING_API}/executions?status=RUNNING`)
}

/**
 * Get recent executions (including completed/failed)
 */
export async function getRecentExecutions(
  limit: number = 50,
  status?: string
): Promise<ExecutionSummary[]> {
  const params = new URLSearchParams({ limit: limit.toString() })
  if (status) params.append('status', status)
  return fetchApi<ExecutionSummary[]>(`${MONITORING_API}/executions?${params}`)
}

/**
 * Get active execution count by phase
 */
export async function getActiveExecutionCount(): Promise<ActiveExecutionCount> {
  return fetchApi<ActiveExecutionCount>(`${MONITORING_API}/dashboard/stats`)
}

/**
 * Get recent executions for a campaign
 */
export async function getCampaignExecutions(
  campaignId: string,
  limit: number = 10
): Promise<ExecutionSummary[]> {
  return fetchApi<ExecutionSummary[]>(
    `${MONITORING_API}/campaigns/${campaignId}/executions?limit=${limit}`
  )
}

/**
 * Get active executions for a campaign
 */
export async function getCampaignActiveExecutions(campaignId: string): Promise<ExecutionProgress[]> {
  return fetchApi<ExecutionProgress[]>(`${MONITORING_API}/campaigns/${campaignId}/executions`)
}

// ============================================================================
// Dashboard Endpoints - via Admin Portal Monitoring API (/api/v1/monitoring/dashboard)
// ============================================================================

/**
 * Get dashboard health status
 */
export async function getDashboardHealth(): Promise<DashboardHealth> {
  return fetchApi<DashboardHealth>(`${MONITORING_API}/dashboard/health`)
}

/**
 * Get active campaigns list
 */
export async function getActiveCampaigns(): Promise<string[]> {
  return fetchApi<string[]>(`${MONITORING_API}/dashboard/campaigns/active`)
}

/**
 * Get catchup events for joining mid-execution
 */
export async function getCatchupEvents(campaignId: string): Promise<DashboardCatchupResponse> {
  return fetchApi<DashboardCatchupResponse>(`${MONITORING_API}/dashboard/campaigns/${campaignId}/catchup`)
}

/**
 * Get recent events with custom time window
 */
export async function getRecentEvents(
  campaignId: string,
  minutes: number = 30,
  limit: number = 100
): Promise<DashboardEvent[]> {
  return fetchApi<DashboardEvent[]>(
    `${MONITORING_API}/dashboard/campaigns/${campaignId}/recent?minutes=${minutes}&limit=${limit}`
  )
}

/**
 * Get complete campaign timeline
 */
export async function getCampaignTimeline(campaignId: string): Promise<DashboardEvent[]> {
  return fetchApi<DashboardEvent[]>(`${MONITORING_API}/dashboard/campaigns/${campaignId}/timeline`)
}

/**
 * Get critical events for alerting
 */
export async function getCriticalEvents(
  campaignId: string,
  hours: number = 24
): Promise<DashboardEvent[]> {
  return fetchApi<DashboardEvent[]>(
    `${MONITORING_API}/dashboard/campaigns/${campaignId}/critical?hours=${hours}`
  )
}

/**
 * Get campaign metrics
 */
export async function getCampaignMetrics(campaignId: string): Promise<CampaignMetrics> {
  return fetchApi<CampaignMetrics>(`${MONITORING_API}/dashboard/campaigns/${campaignId}/metrics`)
}

/**
 * Get execution status
 */
export async function getExecutionStatus(
  executionContextId: string
): Promise<{ executionContextId: string; status: string; timestamp: string }> {
  return fetchApi(`${MONITORING_API}/dashboard/campaigns/${executionContextId}/status`)
}

/**
 * Trigger campaign execution manually
 * Note: This endpoint may still need to go to execution engine for actual triggering
 */
export async function triggerExecution(
  executionContextId: string,
  recordCount?: number
): Promise<TriggerExecutionResponse> {
  const url = recordCount
    ? `${MONITORING_API}/dashboard/campaigns/${executionContextId}/trigger?recordCount=${recordCount}`
    : `${MONITORING_API}/dashboard/campaigns/${executionContextId}/trigger`

  return fetchApi<TriggerExecutionResponse>(url, { method: 'POST' })
}

// ============================================================================
// SSE Stream Helper (for real-time updates)
// Note: SSE streaming still goes to Execution Engine as it requires real-time connection
// ============================================================================

/**
 * Subscribe to execution progress stream (Server-Sent Events)
 * Returns a cleanup function to close the connection
 * Note: This still connects to Execution Engine for real-time streaming
 */
export function subscribeToProgressStream(
  executionId: string,
  onProgress: (progress: ExecutionProgress) => void,
  onError?: (error: Event) => void,
  onComplete?: () => void
): () => void {
  const encodedId = encodeURIComponent(executionId)
  // SSE streaming requires direct connection to execution engine
  const url = `${EXECUTION_API_BASE}/api/v1/executions/progress/stream?executionId=${encodedId}`

  const eventSource = new EventSource(url)

  eventSource.onmessage = (event) => {
    try {
      const progress = JSON.parse(event.data) as ExecutionProgress
      onProgress(progress)

      // Auto-close when execution completes
      if (!progress.isActive) {
        eventSource.close()
        onComplete?.()
      }
    } catch (e) {
      console.error('Failed to parse progress event:', e)
    }
  }

  eventSource.onerror = (error) => {
    console.error('SSE connection error:', error)
    onError?.(error)
    eventSource.close()
  }

  // Return cleanup function
  return () => {
    eventSource.close()
  }
}

// ============================================================================
// Monitoring API Endpoints (/api/v1/monitoring)
// These endpoints provide enriched data with campaign type, A/B test info, iterations
// All go through Admin Portal API
// ============================================================================

/**
 * Get all accounts for filtering
 */
export async function getAccounts(): Promise<AccountSummary[]> {
  return fetchApi<AccountSummary[]>(`${MONITORING_API}/accounts`)
}

/**
 * Get all executions with enriched monitoring data
 * Includes campaign type, A/B test phase, iteration info
 */
export async function getMonitoringExecutions(
  limit: number = 50,
  status?: string,
  campaignType?: CampaignType,
  days?: number,
  accountId?: number
): Promise<ExecutionMonitoringResponse[]> {
  const params = new URLSearchParams({ limit: limit.toString() })

  if (status) params.append('status', status)
  if (campaignType) params.append('campaignType', campaignType)
  if (days !== undefined && !Number.isNaN(days)) {
    params.append('days', days.toString())
  }
  if (accountId !== undefined && !Number.isNaN(accountId)) {
    params.append('accountId', accountId.toString())
  }

  return fetchApi<ExecutionMonitoringResponse[]>(`${MONITORING_API}/executions?${params}`)
}

/**
 * Get detailed execution info including A/B test details and iteration info
 */
export async function getMonitoringExecutionDetail(
  executionId: string
): Promise<ExecutionDetailResponse> {
  const encodedId = encodeURIComponent(executionId)
  return fetchApi<ExecutionDetailResponse>(`${MONITORING_API}/executions/${encodedId}`)
}

/**
 * Get campaign execution history with iterations and A/B test config
 */
export async function getMonitoringCampaignHistory(
  campaignId: string,
  limit: number = 20
): Promise<CampaignExecutionHistoryResponse> {
  return fetchApi<CampaignExecutionHistoryResponse>(
    `${MONITORING_API}/campaigns/${campaignId}/executions?limit=${limit}`
  )
}

/**
 * Get monitoring dashboard statistics
 */
export async function getMonitoringStats(days?: number, accountId?: number): Promise<MonitoringStatsResponse> {
  const params = new URLSearchParams()

  if (days !== undefined && !Number.isNaN(days)) {
    params.append('days', days.toString())
  }
  if (accountId !== undefined && !Number.isNaN(accountId)) {
    params.append('accountId', accountId.toString())
  }

  const queryString = params.toString()
  return fetchApi<MonitoringStatsResponse>(
    `${MONITORING_API}/dashboard/stats${queryString ? `?${queryString}` : ''}`
  )
}

// ============================================================================
// Kafka Metrics API Endpoints (/api/v1/monitoring/kafka)
// All go through Admin Portal API
// ============================================================================

export interface KafkaTopicMetrics {
  topic: string
  totalMessages: number
  todayMessages: number
  dailyCounts: { date: string; count: number }[]
}

export interface KafkaTopicsMetricsResponse {
  timestamp: string
  totalMessages: number
  todayMessages: number
  topics: KafkaTopicMetrics[]
}

export interface KafkaEventsSummaryResponse {
  timestamp: string
  totalEventsToday: number
  totalEventsAllTime: number
  topics: {
    topic: string
    todayCount: number
    totalCount: number
  }[]
}

export interface KafkaDailyCountsResponse {
  topic: string
  days: number
  timestamp: string
  periodTotal: number
  dailyCounts: { date: string; count: number }[]
}

/**
 * Get Kafka metrics for multiple topics
 */
export async function getKafkaTopicsMetrics(
  topics: string[],
  days: number = 7
): Promise<KafkaTopicsMetricsResponse> {
  const params = new URLSearchParams({ days: days.toString() })
  topics.forEach(t => params.append('topics', t))
  return fetchApi<KafkaTopicsMetricsResponse>(`${MONITORING_API}/kafka/topics?${params}`)
}

/**
 * Get Kafka metrics for a single topic
 */
export async function getKafkaTopicMetrics(
  topic: string,
  days: number = 7
): Promise<KafkaTopicMetrics> {
  return fetchApi<KafkaTopicMetrics>(`${MONITORING_API}/kafka/topics/${topic}?days=${days}`)
}

/**
 * Get today's message count for a topic
 */
export async function getKafkaTopicTodayCount(topic: string): Promise<{ topic: string; date: string; count: number }> {
  return fetchApi<{ topic: string; date: string; count: number }>(`${MONITORING_API}/kafka/topics/${topic}/today`)
}

/**
 * Get daily message counts for a topic
 */
export async function getKafkaTopicDailyCounts(
  topic: string,
  days: number = 7
): Promise<KafkaDailyCountsResponse> {
  return fetchApi<KafkaDailyCountsResponse>(`${MONITORING_API}/kafka/topics/${topic}/daily?days=${days}`)
}

/**
 * Get events summary for common event topics (custom-raw, datalist-raw)
 */
export async function getKafkaEventsSummary(): Promise<KafkaEventsSummaryResponse> {
  return fetchApi<KafkaEventsSummaryResponse>(`${MONITORING_API}/kafka/events/summary`)
}
